/**
 * 127: the fields grouping actually reads. Deliberately structural (same convention as
 * `backend/src/modules/cars/serializer.ts`'s `CarVisibilityFields`) so the page's own richer
 * `Car` interface, and lean test fixtures, both satisfy it without a shared import.
 */
export interface LocatedCar {
  location: {
    location_id: string;
    city_name: string;
  };
}

export interface Section<TCar> {
  locationId: string;
  name: string;
  cars: TCar[];
}

/**
 * Groups already-filtered, already-sorted cars into per-location sections (127 FR-001).
 *
 * A section's display name is the matching entry in `locationNames`; when a car's
 * `location_id` has no match there, its section falls back to that car's own `city_name`
 * (127 FR-008, US3) rather than being dropped.
 *
 * Sections order with `nearestLocationId`'s match first (when it names a section actually
 * present), then every other section alphabetically by name (127 FR-004/FR-005). Cars within
 * a section keep the input array's relative order — grouping never re-sorts (127 FR-006).
 */
export function groupCarsByLocation<TCar extends LocatedCar>(
  cars: TCar[],
  locationNames: Map<string, string>,
  nearestLocationId: string | null,
): Section<TCar>[] {
  const sections: Section<TCar>[] = [];
  const byLocationId = new Map<string, Section<TCar>>();

  for (const c of cars) {
    const locationId = c.location.location_id;
    const existing = byLocationId.get(locationId);
    if (existing) {
      existing.cars.push(c);
      continue;
    }
    const section: Section<TCar> = {
      locationId,
      name: locationNames.get(locationId) ?? c.location.city_name,
      cars: [c],
    };
    byLocationId.set(locationId, section);
    sections.push(section);
  }

  sections.sort((a, b) => {
    const aNearest = a.locationId === nearestLocationId;
    const bNearest = b.locationId === nearestLocationId;
    if (aNearest !== bNearest) return aNearest ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return sections;
}
